// ─── Workspace settings (timezone, name, etc.) ────────────────────────────────
// Lightweight client-side mirror of /api/workspaces/me. Fetched once per page
// load and reactive to PATCH /api/workspaces/me. Used by the datetime helpers
// to format dates in the workspace's IANA timezone instead of the browser's.

import React from 'react'
import { apiClient } from '@/lib/api'

export interface WorkspaceSettings {
  id: string | null
  name: string | null
  timezone: string
}

const STORAGE_KEY = 'workspace.settings.v1'
const FALLBACK_TZ = 'America/Chicago'

const DEFAULTS: WorkspaceSettings = {
  id: null,
  name: null,
  timezone: FALLBACK_TZ,
}

function loadFromStorage(): WorkspaceSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as Partial<WorkspaceSettings>
    return {
      id: parsed.id ?? null,
      name: parsed.name ?? null,
      timezone: parsed.timezone || FALLBACK_TZ,
    }
  } catch {
    return DEFAULTS
  }
}

let _workspacePromise: Promise<WorkspaceSettings> | null = null

function fetchWorkspaceOnce(): Promise<WorkspaceSettings> {
  if (!_workspacePromise) {
    _workspacePromise = apiClient
      .get<{ success: boolean; data: { id: string; name: string; timezone: string } }>(
        '/api/workspaces/me',
      )
      .then((res) => {
        const data = res.data.data
        const ws: WorkspaceSettings = {
          id: data.id,
          name: data.name,
          timezone: data.timezone || FALLBACK_TZ,
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ws))
        return ws
      })
      .catch(() => {
        _workspacePromise = null
        return loadFromStorage()
      })
  }
  return _workspacePromise
}

export async function saveWorkspaceTimezone(timezone: string): Promise<WorkspaceSettings> {
  const res = await apiClient.patch<{
    success: boolean
    data: { id: string; name: string; timezone: string }
  }>('/api/workspaces/me', { timezone })
  const data = res.data.data
  const ws: WorkspaceSettings = {
    id: data.id,
    name: data.name,
    timezone: data.timezone || FALLBACK_TZ,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ws))
  _workspacePromise = null
  window.dispatchEvent(new CustomEvent('workspace-settings-updated', { detail: ws }))
  return ws
}

/** Reactive hook — returns workspace settings, refreshed on save events. */
export function useWorkspace(): WorkspaceSettings {
  const [ws, setWs] = React.useState<WorkspaceSettings>(() => loadFromStorage())

  React.useEffect(() => {
    let active = true
    fetchWorkspaceOnce().then((fetched) => {
      if (active) setWs(fetched)
    })
    return () => { active = false }
  }, [])

  React.useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<WorkspaceSettings>
      if (custom.detail) setWs(custom.detail)
    }
    window.addEventListener('workspace-settings-updated', handler)
    return () => window.removeEventListener('workspace-settings-updated', handler)
  }, [])

  return ws
}

/**
 * Synchronous read of the workspace timezone — for callers outside React
 * (formatters, sorters, event-loop tasks). Returns the cached value; falls
 * back to the IANA default. Pair with useWorkspace() inside React components.
 */
export function getWorkspaceTimezone(): string {
  return loadFromStorage().timezone || FALLBACK_TZ
}
